import { magasinIndexedDB, type Magasin, type ReponseEnFile } from './magasin'
import { api, ApiError } from '@/lib/api/client'
import type { ReponseAEnvoyer, ResultatDeSynchronisation } from '@/lib/api/feed'

/**
 * La file des réponses données hors connexion — RG-9.4, [DECISIONS §111].
 *
 * **Ce qu'elle remplace : rien.** Le Fil attrapait l'échec réseau et rendait la
 * main en commentant « le candidat retapera ». Cela suppose qu'il sache qu'il
 * doit retaper — rien ne le lui disait — et qu'il ait encore la carte sous les
 * yeux, ce qui cesse d'être vrai dès qu'il fait défiler. Sur le parc visé, ce
 * n'est pas un cas limite : c'est le trajet en tap-tap, la coupure de courant,
 * le quartier sans couverture. Une séance entière disparaissait sans un message.
 *
 * **L'idempotence était déjà là et n'attendait que ça.** `client_uuid` est
 * généré AVANT l'envoi, l'index unique du serveur ignore ce qu'il connaît déjà,
 * et `accepted` rend les identifiants que le client peut oublier (CA-9.2,
 * 05 §7.2 étapes 5 et 6). On ne supprime QUE ce qui est confirmé : un lot envoyé
 * deux fois ne crée pas de doublon, un lot perdu en route repart.
 *
 * **Écrite à la main, sans Dexie** (que 05 §7.2 nomme) : un magasin, quatre
 * opérations, aucune migration. 25 Kio de dépendance pour cela pèseraient sur le
 * premier chargement, celui qu'on paie sur une 3G.
 */

/** Le lot d'envoi — 05 §7.2, étape 4. Cinquante tient dans une requête lente. */
const PAR_LOT = 50

export type EtatDuReseau = 'en-ligne' | 'hors-ligne' | 'synchronisation'

export interface EtatDeLaFile {
  etat: EtatDuReseau
  enAttente: number
}

let magasin: Magasin = magasinIndexedDB()

/** Pour les tests : substitue le magasin et remet l'état à zéro. */
export function poserLeMagasin(remplacant: Magasin): void {
  magasin = remplacant
  etatCourant = { etat: 'en-ligne', enAttente: 0 }
  enCours = null
}

// ── L'état, observable ────────────────────────────────────────────────────

const ecouteurs = new Set<(e: EtatDeLaFile) => void>()

let etatCourant: EtatDeLaFile = {
  etat: typeof navigator !== 'undefined' && !navigator.onLine ? 'hors-ligne' : 'en-ligne',
  enAttente: 0,
}

function poser(suite: Partial<EtatDeLaFile>): void {
  etatCourant = { ...etatCourant, ...suite }
  ecouteurs.forEach((e) => { e(etatCourant) })
}

export function etatDeLaFile(): EtatDeLaFile {
  return etatCourant
}

export function abonner(ecouteur: (e: EtatDeLaFile) => void): () => void {
  ecouteurs.add(ecouteur)

  return () => { ecouteurs.delete(ecouteur) }
}

// ── Les dévoilements qui arrivent en retard ───────────────────────────────

const retardataires = new Set<(reveals: ResultatDeSynchronisation['reveals']) => void>()

/**
 * **Le verdict revient s'il peut** — §111. À la reconnexion, le serveur renvoie
 * les dévoilements du lot, indexés par `client_uuid` : les cartes encore à
 * l'écran s'affichent alors corrigées. Celles qu'on a fait défiler, non — et
 * rien ne le promet.
 */
export function auRetourDesVerdicts(
  ecouteur: (reveals: ResultatDeSynchronisation['reveals']) => void,
): () => void {
  retardataires.add(ecouteur)

  return () => { retardataires.delete(ecouteur) }
}

// ── L'écriture ────────────────────────────────────────────────────────────

export async function mettreEnFile(lot: ReponseAEnvoyer[], contexte: string): Promise<void> {
  for (const reponse of lot) {
    await magasin.ecrire({ ...reponse, contexte })
  }

  poser({ enAttente: await magasin.compter(), etat: 'hors-ligne' })
}

export function taille(): Promise<number> {
  return magasin.compter()
}

// ── L'envoi ───────────────────────────────────────────────────────────────

let enCours: Promise<void> | null = null

/**
 * Vide la file, du plus ancien au plus récent.
 *
 * **Sérialisé** : deux vidages concurrents — l'événement `online` et un envoi
 * réussi, par exemple — enverraient le même lot deux fois. Le serveur y
 * survivrait (CA-9.2), la file locale moins bien : le second effacerait des
 * entrées que le premier était en train de renvoyer.
 */
export function vider(): Promise<void> {
  enCours ??= viderVraiment().finally(() => { enCours = null })

  return enCours
}

async function viderVraiment(): Promise<void> {
  if ((await magasin.compter()) === 0) {
    poser({ etat: 'en-ligne', enAttente: 0 })

    return
  }

  poser({ etat: 'synchronisation', enAttente: await magasin.compter() })

  for (;;) {
    const lot = await prochainLot()

    if (lot.length === 0) break

    // **Sans progrès, on s'arrête** — sinon la boucle repart sur le même lot
    // indéfiniment. Un serveur qui répond 200 en ne confirmant rien — bogue,
    // déploiement à moitié fait, proxy qui avale le corps — transformait le
    // client en marteau : une requête par milliseconde, sur la connexion
    // comptée du candidat. Constaté ici en épuisant la mémoire du test.
    const avant = await magasin.compter()

    // Un lot ne mélange pas les contextes : c'est le contexte qui décide si la
    // réponse compte pour l'objectif du jour (§101), et il est envoyé une fois
    // pour tout le lot. On prend donc celles qui partagent le contexte de la
    // plus ancienne ; les autres suivront au tour suivant, dans leur ordre.
    const contexte = lot[0]?.contexte ?? 'feed'
    const homogene = lot.filter((r) => r.contexte === contexte)

    let resultat: ResultatDeSynchronisation

    try {
      resultat = await api<ResultatDeSynchronisation>('/responses', {
        method: 'POST',
        body: JSON.stringify({
          context: contexte,
          // Le serveur en tient compte pour la résolution des conflits de
          // répétition espacée (RG-9.5).
          was_offline: true,
          responses: homogene.map(sansContexte),
        }),
      })
    } catch (erreur) {
      if (erreur instanceof ApiError && erreur.status >= 400 && erreur.status < 500) {
        // **Une réponse que le serveur refuse ne repartira jamais** — §111. La
        // renvoyer bouclerait, et la file grossirait indéfiniment sur une
        // erreur que personne ne voit.
        await oublier(homogene.map((r) => r.client_uuid))

        const restant = await magasin.compter()

        poser({ enAttente: restant })

        // Même garde que plus bas : si le retrait n'a rien retiré, le tour
        // suivant repartirait sur le même lot.
        if (restant >= avant) break

        continue
      }

      // Le réseau, ou le serveur, n'est pas là. On s'arrête : la suite de la
      // file part au prochain essai, dans l'ordre.
      poser({ etat: 'hors-ligne', enAttente: await magasin.compter() })

      return
    }

    // Ce que le serveur n'a pas confirmé RESTE en file (05 §7.2, étape 6) —
    // sauf ce qu'il a nommément refusé, qui ne passera pas davantage demain.
    await oublier([...resultat.accepted, ...Object.keys(resultat.rejected)])

    if (Object.keys(resultat.reveals).length > 0) {
      retardataires.forEach((e) => { e(resultat.reveals) })
    }

    const apres = await magasin.compter()

    poser({ enAttente: apres })

    // Rien n'est parti : le prochain tour renverrait exactement le même lot.
    // On rend la main — le vidage suivant réessaiera, plus tard.
    if (apres >= avant) break
  }

  poser({ etat: 'en-ligne', enAttente: await magasin.compter() })
}

/**
 * Les plus anciennes d'abord — RG-9.5.
 *
 * **L'ordre est une règle, pas un détail de stockage.** La répétition espacée
 * rejoue les états par `answered_at` croissant ; une file renvoyée dans le
 * désordre donnerait un état de rappel faux, et le serveur ne peut pas rétablir
 * un ordre qu'on lui a fait perdre. Le tri vit donc ici, où il se teste, et non
 * dans un index de base.
 */
async function prochainLot(): Promise<ReponseEnFile[]> {
  const toutes = await magasin.lire()

  return toutes
    .sort((a, b) => a.answered_at.localeCompare(b.answered_at))
    .slice(0, PAR_LOT)
}

async function oublier(uuids: string[]): Promise<void> {
  for (const uuid of uuids) {
    await magasin.effacer(uuid)
  }
}

/** La file porte un champ que l'API ne connaît pas. */
function sansContexte(r: ReponseEnFile): ReponseAEnvoyer {
  return {
    client_uuid: r.client_uuid,
    form_version_id: r.form_version_id,
    shown_option_ids: r.shown_option_ids,
    submitted: r.submitted,
    answered_at: r.answered_at,
    time_spent_ms: r.time_spent_ms,
  }
}

// ── Le branchement ────────────────────────────────────────────────────────

/**
 * Écoute le réseau et vide la file quand il revient.
 *
 * Appelée une fois au démarrage. `online` ment parfois — le portail captif d'un
 * cybercafé rend `navigator.onLine` vrai sans laisser passer une requête — mais
 * il ne coûte rien : un vidage qui échoue laisse la file intacte.
 */
export function brancherLaFile(): () => void {
  const auRetour = (): void => { void vider() }
  const auDepart = (): void => { poser({ etat: 'hors-ligne' }) }

  window.addEventListener('online', auRetour)
  window.addEventListener('offline', auDepart)

  // Au démarrage : une séance peut dormir en file depuis la veille.
  void magasin.compter().then((n) => {
    poser({ enAttente: n })

    if (n > 0 && navigator.onLine) void vider()
  })

  return () => {
    window.removeEventListener('online', auRetour)
    window.removeEventListener('offline', auDepart)
  }
}
